/**
 * Partner Portal — Business Details onboarding (Slice B — Feb 2026 July).
 *
 * Persistent yellow banner on every partner page (via BusinessDetailsBanner)
 * drives the franchisee here. This form collects the legally required
 * business entity data before we can set up payouts:
 *
 *   • Legal business name (as it appears on your ABN)
 *   • ABN (11 digits, checksum validated locally + server-side)
 *   • GST registered? (bool)
 *   • Physical address (structured AU address, Places-assisted)
 *   • Postal address (either same-as-physical or its own structured record)
 *
 * Saving is fail-soft: partial saves land as "incomplete" so the partner
 * can pick back up later.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Save, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { useOnboardingStatus } from "@/lib/onboardingStatus";
import { PartnerShell } from "@/pages/partner/Dashboard";
import AddressStructuredForm from "@/components/partner/AddressStructuredForm";

const EMPTY_ADDR = { line1: "", line2: "", suburb: "", state: "", postcode: "", formatted: "" };

const EMPTY_FORM = {
  legal_name: "",
  trading_name: "",
  abn: "",
  gst_registered: false,
  physical_address: { ...EMPTY_ADDR },
  postal_address: { ...EMPTY_ADDR },
  postal_same_as_physical: true,
};

/** Very light client-side ABN checksum for instant feedback. Server is
 *  the source of truth — this is UX only. */
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
function abnClientValid(raw) {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length !== 11) return false;
  const nums = d.split("").map((c) => parseInt(c, 10));
  nums[0] -= 1;
  const total = nums.reduce((sum, n, i) => sum + n * ABN_WEIGHTS[i], 0);
  return total % 89 === 0;
}

function StatusPill({ status }) {
  const tone = {
    missing:    "bg-slate-100 text-slate-700 border-slate-200",
    incomplete: "bg-amber-50 text-amber-800 border-amber-200",
    complete:   "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = { missing: "Not started", incomplete: "In progress", complete: "Complete" }[status] || status;
  return (
    <span data-testid="business-details-status-pill" className={`text-xs px-2.5 py-1 rounded-full border font-medium ${tone}`}>
      {label}
    </span>
  );
}

export default function BusinessDetailsPage() {
  useSEO({ title: "Business Details — Aware Partner Portal", noindex: true, includeBaseline: false });
  const { refresh: refreshOnboarding } = useOnboardingStatus();

  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErr, setFieldErr] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/business-details");
        setForm({
          legal_name:              data.legal_name || "",
          trading_name:            data.trading_name || "",
          abn:                     data.abn || "",
          gst_registered:          !!data.gst_registered,
          physical_address:        data.physical_address || { ...EMPTY_ADDR },
          postal_address:          data.postal_address || { ...EMPTY_ADDR },
          postal_same_as_physical: data.postal_same_as_physical ?? true,
        });
        setSaved(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Unable to load business details.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const abnLocalValid = useMemo(() => (form.abn ? abnClientValid(form.abn) : null), [form.abn]);

  const patchForm = (partial) => setForm((f) => ({ ...f, ...partial }));
  const patchAddr = (kind, next) => setForm((f) => ({ ...f, [kind]: next }));

  const onSave = async () => {
    setSaving(true); setError(null); setFieldErr({});
    try {
      const payload = { ...form };
      // If postal-same-as-physical, mirror the physical address before sending
      if (form.postal_same_as_physical) {
        payload.postal_address = form.physical_address;
      }
      const { data } = await api.put("/partner/business-details", payload);
      setSaved(data);
      setForm({
        legal_name: data.legal_name || "",
        trading_name: data.trading_name || "",
        abn: data.abn || "",
        gst_registered: !!data.gst_registered,
        physical_address: data.physical_address || { ...EMPTY_ADDR },
        postal_address: data.postal_address || { ...EMPTY_ADDR },
        postal_same_as_physical: data.postal_same_as_physical ?? true,
      });
      refreshOnboarding();
      toast.success(data.status === "complete" ? "Business details saved and complete" : "Draft saved — a few fields still need completing");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        setFieldErr({ [detail.field]: detail.message });
        setError(null);
      } else {
        setError(typeof detail === "string" ? detail : "Failed to save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PartnerShell active="business details">
        <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading business details…
        </div>
      </PartnerShell>
    );
  }

  const status = saved?.status || "missing";

  return (
    <PartnerShell active="business details">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="business-details-page">
        <div>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Partner Onboarding</div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Business details</h1>
            </div>
            <StatusPill status={status} />
          </div>
          <p className="mt-3 text-sm text-slate-600 max-w-xl">
            We need these details to set up commission payouts and issue tax-compliant invoices.
            Your information stays private and is only shared with the AWARE finance team.
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm flex items-start gap-2" data-testid="business-details-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" /> {error}
          </div>
        )}

        {/* Legal identity */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Legal identity</h2>

          <div>
            <label className="text-xs font-medium text-slate-600">Legal business name *</label>
            <Input
              data-testid="business-details-legal-name"
              value={form.legal_name}
              onChange={(e) => patchForm({ legal_name: e.target.value })}
              placeholder="ACME Water Solutions Pty Ltd"
              className="mt-1"
            />
            <p className="text-[11px] text-slate-500 mt-1">Must match the name registered against your ABN.</p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Trading name (optional)</label>
            <Input
              data-testid="business-details-trading-name"
              value={form.trading_name}
              onChange={(e) => patchForm({ trading_name: e.target.value })}
              placeholder="Aware Water Noosa"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">ABN *</label>
            <Input
              data-testid="business-details-abn"
              value={form.abn}
              onChange={(e) => patchForm({ abn: e.target.value.replace(/[^0-9\s-]/g, "").slice(0, 20) })}
              placeholder="51 824 753 556"
              className="mt-1 font-mono"
              inputMode="numeric"
            />
            <div className="flex items-center gap-2 mt-1 text-[11px]">
              {form.abn && (
                abnLocalValid ? (
                  <span className="text-emerald-700 inline-flex items-center gap-1" data-testid="business-details-abn-valid">
                    <CheckCircle2 className="w-3 h-3" /> Valid checksum
                  </span>
                ) : (
                  <span className="text-rose-700 inline-flex items-center gap-1" data-testid="business-details-abn-invalid">
                    <AlertTriangle className="w-3 h-3" /> ABN checksum looks wrong
                  </span>
                )
              )}
              {fieldErr.abn && <span className="text-rose-700">{fieldErr.abn}</span>}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <input
              id="gst-registered"
              data-testid="business-details-gst"
              type="checkbox"
              checked={form.gst_registered}
              onChange={(e) => patchForm({ gst_registered: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            <label htmlFor="gst-registered" className="text-sm text-slate-800">Registered for GST</label>
          </div>
        </section>

        {/* Physical address */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Physical address</h2>
          {fieldErr.physical_address && (
            <div className="text-xs text-rose-700" data-testid="business-details-physical-error">{fieldErr.physical_address}</div>
          )}
          <AddressStructuredForm
            testIdPrefix="business-details-physical"
            value={form.physical_address}
            onChange={(next) => patchAddr("physical_address", next)}
          />
        </section>

        {/* Postal address */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">Postal address</h2>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="business-details-postal-same"
                checked={form.postal_same_as_physical}
                onChange={(e) => patchForm({ postal_same_as_physical: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
              Same as physical
            </label>
          </div>
          {fieldErr.postal_address && (
            <div className="text-xs text-rose-700" data-testid="business-details-postal-error">{fieldErr.postal_address}</div>
          )}
          {form.postal_same_as_physical ? (
            <div className="text-xs text-slate-500 flex items-start gap-2" data-testid="business-details-postal-mirror">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-none" />
              Using the same address as above.
            </div>
          ) : (
            <AddressStructuredForm
              testIdPrefix="business-details-postal"
              value={form.postal_address}
              onChange={(next) => patchAddr("postal_address", next)}
            />
          )}
        </section>

        {/* Save row */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-xs text-slate-500">
            {saved?.updated_at ? (
              <>Last saved {new Date(saved.updated_at).toLocaleString("en-AU")}
              {saved.completed_at && <> · Marked complete on {new Date(saved.completed_at).toLocaleDateString("en-AU")}</>}</>
            ) : "Not saved yet"}
          </div>
          <Button
            data-testid="business-details-save-btn"
            onClick={onSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-cyan-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? "Saving…" : "Save details"}
          </Button>
        </div>
      </div>
    </PartnerShell>
  );
}
